import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '~/Store/hooks'
import { getDefaultOrganization } from '~/Store/User/selectors'
import {
  LogEntry,
  LogSearchResult,
  LogSummaryResult,
  OrganizationsService,
} from '~/Services/OrganizationService'
import { errorNotification } from '~/Services'
import styles from './AuditLog.module.less'

const { Option } = Select

// Preset khoảng thời gian -> tránh phụ thuộc moment/dayjs của antd RangePicker cho MVP.
const RANGE_DAYS: Record<string, number | undefined> = {
  '7': 7,
  '30': 30,
  '90': 90,
  all: undefined,
}

const fmtTime = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// Nhãn ngày cho biểu đồ. key có thể là "yyyy-MM-dd" (BE ép format) hoặc "MM/dd/yyyy"
// (tùy mapping index cũ) -> parse linh hoạt, hiển thị dd/MM. Không cắt chuỗi theo vị trí cứng.
const dayLabel = (key?: string) => {
  if (!key) return ''
  const iso = /^\d{4}-\d{2}-\d{2}/.test(key) // "yyyy-MM-dd..." -> tách tay tránh lệch timezone
  if (iso) {
    const p = key.slice(0, 10).split('-') // [yyyy, MM, dd]
    return `${p[2]}/${p[1]}`
  }
  const dt = new Date(key)
  if (!isNaN(dt.getTime())) {
    const dd = String(dt.getDate()).padStart(2, '0')
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}`
  }
  return key
}

// Giá trị before/after để hiển thị: phân biệt rỗng/null với chuỗi "" cho dễ đọc.
const fmtVal = (v: any) => {
  if (v === null || v === undefined || v === '') return '(trống)'
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

const resultColor = (r?: string) =>
  r === 'Success' ? 'green' : r === 'Failure' ? 'red' : 'default'

const categoryColor = (c?: string) =>
  c === 'audit' ? 'blue' : c === 'activity' ? 'geekblue' : 'default'

export const AuditLog = () => {
  const { t } = useTranslation()
  const defaultOrg = useAppSelector(getDefaultOrganization)
  const orgId = defaultOrg?.guid

  const [category, setCategory] = useState<string>('all')
  const [eventType, setEventType] = useState<string | undefined>(undefined)
  const [rangeKey, setRangeKey] = useState<string>('7')
  const [search, setSearch] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(20)

  const [summary, setSummary] = useState<LogSummaryResult | null>(null)
  const [list, setList] = useState<LogSearchResult | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  // from/to (ISO) suy ra từ preset — memo theo rangeKey.
  const { from, to } = useMemo(() => {
    const days = RANGE_DAYS[rangeKey]
    if (!days) return { from: undefined, to: undefined }
    const f = new Date(Date.now() - days * 86400000).toISOString()
    return { from: f, to: undefined as string | undefined }
  }, [rangeKey])

  const baseParams = useCallback(
    () => ({
      category: category === 'all' ? undefined : category,
      eventType: eventType || undefined,
      search: search || undefined,
      from,
      to,
    }),
    [category, eventType, search, from, to],
  )

  const fetchAll = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const [sumRes, listRes]: any = await Promise.all([
        OrganizationsService.Get.getOrganizationLogSummary(orgId, baseParams()),
        OrganizationsService.Get.getOrganizationLogs(orgId, {
          ...baseParams(),
          page,
          pageSize,
        }),
      ])
      setSummary(sumRes.data as LogSummaryResult)
      setList(listRes.data as LogSearchResult)
    } catch (error) {
      errorNotification(error)
    } finally {
      setLoading(false)
    }
  }, [orgId, baseParams, page, pageSize])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Reset về trang 1 khi đổi bộ lọc.
  useEffect(() => {
    setPage(1)
  }, [category, eventType, rangeKey, search])

  const notConfigured =
    (summary && summary.configured === false) ||
    (list && list.configured === false)

  const successRate = useMemo(() => {
    if (!summary || !summary.total) return 0
    const ok =
      summary.byResult.find(b => b.key === 'Success')?.count ?? 0
    return Math.round((ok / summary.total) * 100)
  }, [summary])

  const maxDay = useMemo(
    () => Math.max(1, ...(summary?.byDay || []).map(b => b.count)),
    [summary],
  )

  const eventTypeOptions = summary?.byEventType || []

  const columns = [
    {
      title: t('auditLog.colTime'),
      dataIndex: 'timestamp',
      width: 170,
      render: (v: string) => fmtTime(v),
    },
    {
      title: t('auditLog.colCategory'),
      dataIndex: 'category',
      width: 100,
      render: (v: string) => <Tag color={categoryColor(v)}>{v}</Tag>,
    },
    {
      title: t('auditLog.colEventType'),
      dataIndex: 'eventType',
      width: 150,
      render: (v: string) => <strong>{v}</strong>,
    },
    {
      title: t('auditLog.colUser'),
      dataIndex: 'username',
      render: (v: string, r: LogEntry) => v || r.userId || '-',
    },
    {
      title: t('auditLog.colAction'),
      dataIndex: 'action',
      width: 110,
      render: (v: string) => v || '-',
    },
    {
      title: t('auditLog.colEntity'),
      dataIndex: 'entityType',
      render: (v: string, r: LogEntry) =>
        v ? (
          <Tooltip title={r.entityId}>
            {v}
            {r.entityId ? ` · ${String(r.entityId).slice(0, 8)}…` : ''}
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: t('auditLog.colResult'),
      dataIndex: 'result',
      width: 100,
      render: (v: string) => <Tag color={resultColor(v)}>{v || '-'}</Tag>,
    },
  ]

  if (!orgId) return null

  return (
    <div>
      <Typography.Title level={4}>{t('auditLog.title')}</Typography.Title>

      {notConfigured ? (
        <Alert
          type="warning"
          showIcon
          message={t('auditLog.notConfigured')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* Bộ lọc */}
      <div className={styles.filters}>
        <Select
          value={category}
          onChange={setCategory}
          style={{ width: 170 }}
        >
          <Option value="all">{t('auditLog.categoryAll')}</Option>
          <Option value="audit">{t('auditLog.categoryAudit')}</Option>
          <Option value="activity">{t('auditLog.categoryActivity')}</Option>
        </Select>

        <Select
          allowClear
          placeholder={t('auditLog.filterEventType')}
          value={eventType}
          onChange={v => setEventType(v)}
          style={{ width: 200 }}
        >
          {eventTypeOptions.map(o => (
            <Option key={o.key} value={o.key}>
              {o.key} ({o.count})
            </Option>
          ))}
        </Select>

        <Select
          value={rangeKey}
          onChange={setRangeKey}
          style={{ width: 150 }}
        >
          <Option value="7">{t('auditLog.last7days')}</Option>
          <Option value="30">{t('auditLog.last30days')}</Option>
          <Option value="90">90</Option>
          <Option value="all">{t('auditLog.categoryAll')}</Option>
        </Select>

        <Input.Search
          allowClear
          placeholder={t('auditLog.search')}
          onSearch={v => setSearch(v)}
          style={{ width: 280 }}
        />

        <Tooltip title={t('auditLog.refresh')}>
          <ReloadOutlined
            onClick={fetchAll}
            style={{ fontSize: 18, cursor: 'pointer' }}
          />
        </Tooltip>
      </div>

      <Spin spinning={loading}>
        {/* Dashboard cards */}
        <Row gutter={16} className={styles.cards}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('auditLog.totalEvents')}
                value={summary?.total ?? 0}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('auditLog.eventTypes')}
                value={summary?.byEventType.length ?? 0}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={t('auditLog.successRate')}
                value={successRate}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* Biểu đồ + phân bố */}
        <Row gutter={16} className={styles.cards}>
          <Col xs={24} md={16}>
            <Card title={t('auditLog.byDay')} size="small">
              {summary && summary.byDay.length > 0 ? (
                <div className={styles.bars}>
                  {summary.byDay.map(b => (
                    <div className={styles.barWrap} key={b.key}>
                      <span className={styles.barCount}>{b.count || ''}</span>
                      <div
                        className={styles.bar}
                        style={{ height: `${(b.count / maxDay) * 100}%` }}
                      />
                      <span className={styles.barLabel}>{dayLabel(b.key)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title={t('auditLog.byEventType')} size="small">
              {summary && summary.byEventType.length > 0 ? (
                summary.byEventType.slice(0, 8).map(b => (
                  <div className={styles.eventTypeItem} key={b.key}>
                    <span>{b.key}</span>
                    <Tag>{b.count}</Tag>
                  </div>
                ))
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
        </Row>

        {/* Bảng chi tiết */}
        <Table<LogEntry>
          size="small"
          columns={columns as any}
          dataSource={list?.items || []}
          rowKey={(r: any) =>
            (r.raw && (r.raw.eventId as string)) ||
            `${r.timestamp}-${r.eventType}`
          }
          locale={{ emptyText: t('auditLog.noData') }}
          expandable={{
            expandedRowRender: (r: LogEntry) => {
              const raw: any = r.raw || {}
              const before = raw.before as Record<string, any> | null
              const after = raw.after as Record<string, any> | null
              const keys = after ? Object.keys(after) : []
              return (
                <>
                  {keys.length > 0 && (
                    <div className={styles.changes}>
                      <div className={styles.changesTitle}>
                        {before
                          ? t('auditLog.changes')
                          : t('auditLog.initialValues')}
                      </div>
                      {keys.map(k => (
                        <div className={styles.changeRow} key={k}>
                          <span className={styles.changeField}>{k}</span>
                          {before && (
                            <>
                              <span className={styles.changeOld}>
                                {fmtVal(before[k])}
                              </span>
                              <span className={styles.changeArrow}>→</span>
                            </>
                          )}
                          <span className={styles.changeNew}>
                            {fmtVal(after![k])}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <pre className={styles.rawPre}>
                    {JSON.stringify(r.raw, null, 2)}
                  </pre>
                </>
              )
            },
          }}
          pagination={{
            current: list?.page || page,
            pageSize: list?.pageSize || pageSize,
            total: list?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps || 20)
            },
          }}
        />
      </Spin>
    </div>
  )
}
